<template>
    <main>

        <integration-connector
            :active="integrationActive"
            :chosen="prelaunched"
            :connectLabel="connectLabel"
            :disconnectLabel="disconnectLabel"
            :altDescription="extraInfoDescription"
            logo-classes="bg-seek hover:bg-seek-light"
            icon="seek-color"
            translation-key="seek"
            :show-pill="!integrationInactive"
            :pill-color="pillColor"
            :pill-description="pillDescription"
            pill-text-color="white"
            :show-extra-button="integrationInactive"
            :extra-button-arrow-direction="accordionOpen ? 'up' : 'down'"
            :extraButtonLabel="$t('general.discover')"
            :collapse="accordionOpen"
            @extraButton="accordionOpen = !accordionOpen"
            @connect="showHirerPopup()"
            @disconnect="showDisconnectPopup()"
        >
            <template #collapse>
                <SeekDiscover v-if="integrationInactive" />
            </template>
        </integration-connector>

        <popup-modal-confirm v-if="hirerPopupVisible"
                             :title="$t('settings.integrations.seek.popup_title')"
                             :message="$t('settings.integrations.seek.popup_message')"
                             :confirmText="$t('settings.integrations.seek.popup_confirmation')"
                             :working="performingRequest"
                             :disabled="disallowRequest"
                             :hasCloseButton="true"
                             :showCancelButton="false"
                             confirm-button="success"
                             @confirm="requestIntegration"
                             @close="hideHirerPopup">
            <div>
                <label>{{ $t('settings.integrations.seek.hirer_id') }}</label>
                <simple-input
                    autofocus
                    type="text"
                    name="hirerId"
                    autocomplete="off"
                    v-model="form.hirerId"
                    :errors="form.errors.hirerId"
                    :placeholder="$t('settings.integrations.seek.hirer_id')"
                    @input="hirerChanged"
                >
                </simple-input>
            </div>
        </popup-modal-confirm>

        <popup-modal-confirm v-if="disconnectPopupVisible"
                             :title="$t('settings.integrations.seek.disconnect_popup_title')"
                             :message="$t('settings.integrations.seek.disconnect_popup_message')"
                             :confirmText="disconnectLabel"
                             confirmButton="bg-red-light hover:bg-red"
                             :working="performingRequest"
                             :showExclamation="true"
                             :hasCloseButton="true"
                             :showCancelButton="false"
                             @confirm="disconnect"
                             @close="hideDisconnectPopup">
        </popup-modal-confirm>

    </main>
</template>

<script>
import {ADD_NOTIFICATION} from '../../state/mutation-types'
import {mapState, mapActions} from 'pinia'
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import {useCompaniesStore} from '../../state/modules/companies';
import Form from "../../etc/forms/Form";
import Input from '../forms/Input.vue'
import IntegrationConnector from './IntegrationConnector.vue'
import PopupModalConfirm from '../ui/PopupModalConfirm'
import SeekDiscover from "./SeekDiscover.vue"
import * as seek from '../../services/SeekService'

export default {
    components: {
        'integration-connector': IntegrationConnector,
        'simple-input': Input,
        PopupModalConfirm,
        SeekDiscover,
    },
    data: function () {
        return {
            form: new Form([
                {name: 'hirerId', value: '', rules: []},
            ]),
            state: 'inactive',
            accordionOpen: false,
            hirerPopupVisible: false,
            disconnectPopupVisible: false,
            disallowRequest: true,
            performingRequest: false,
        }
    },
    props: {
        prelaunched: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        integrationInactive() {
            return this.state === 'inactive'
        },
        integrationActive() {
            return this.state === 'active'
        },
        connectLabel() {
            return this.integrationInactive ? this.$t('settings.integrations.seek.activate') : null
        },
        disconnectLabel() {
            return this.$t('settings.integrations.seek.disconnect')
        },
        pillColor() {
            return this.integrationActive ? 'green' : null
        },
        pillDescription() {
            return this.integrationActive ? this.$t('general.active') : null
        },
        extraInfoDescription() {
            return this.integrationActive ? this.$t('settings.integrations.seek.description_active') : this.$t('settings.integrations.seek.description')
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        ...mapActions(useCompaniesStore, ['setSeekStatus']),
        showHirerPopup() {
            this.hirerPopupVisible = true
        },
        hideHirerPopup() {
            this.hirerPopupVisible = false
            this.resetForm();
        },
        showDisconnectPopup() {
            this.disconnectPopupVisible = true
        },
        hideDisconnectPopup() {
            this.disconnectPopupVisible = false
        },
        closeAccordion() {
            this.accordionOpen = false
        },
        requestIntegration() {
            this.performingRequest = true

            this.form.submit(data => seek.requestIntegration({ hirerId: data.hirerId }))
                .then(() => {
                    this.setState('active')
                    this.closeAccordion()
                    this.hideHirerPopup()
                    this.showSuccessMessage()
                })
                .finally(() => this.performingRequest = false)
        },
        disconnect() {
            this.performingRequest = true

            seek.disconnect()
                .then(() => {
                    this.resetForm();
                    this.setState('inactive')
                    this.hideDisconnectPopup()
                    this.showDisconnectMessage()
                })
                .finally(() => this.performingRequest = false)
        },
        resetForm() {
            this.form.hirerId = ''
            this.form.resetErrors();
        },
        setState(state) {
            this.setSeekStatus(state)
            this.state = state
        },
        getState() {
            const state = this.currentCompany.seek_status ?? ''
            return state.trim().length > 0 ? state : 'inactive'
        },
        showSuccessMessage() {
            this[ADD_NOTIFICATION]({
                type: 'success',
                message: this.$t('settings.integrations.seek.success'),
            })
        },
        showDisconnectMessage() {
            this[ADD_NOTIFICATION]({
                type: 'success',
                message: this.$t('settings.integrations.seek.disconnected'),
            })
        },
        hirerChanged(value) {
            this.disallowRequest = !value || value.length === 0
        },
    },
    mounted() {
        this.state = this.getState()
    }
}
</script>
